import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const InstitutionenContainer = styled.div`
  min-height: 100vh;
  padding-top: 100px;
`;

const HeroSection = styled.section`
  position: relative;
  height: 20vh;
  background: ${props => props.theme.colors.gradients.hero};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(3rem, 6vw, 4rem);
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.4);
`;

const ContentSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const ServiceCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.card};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${props => props.theme.shadows.hover};
  }
`;

const ServiceIcon = styled.div`
  height: 150px;
  background: ${props => props.gradient || props.theme.colors.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: white;
`;

const ServiceContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  
  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${props => props.theme.colors.textLight};
    line-height: 1.6;
    margin-bottom: 1.5rem;
    flex-grow: 1;
  }
  
  .contact-info {
    display: inline-block;
    padding: 1rem 2rem;
    background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
    color: white;
    text-decoration: none;
    border-radius: 25px;
    font-weight: 600;
    font-size: 1rem;
    transition: all 0.3s ease;
    box-shadow: 0 5px 15px rgba(46, 204, 113, 0.4);
    text-align: center;
    cursor: pointer;
    border: none;
    align-self: center;
    margin-top: auto;
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 25px rgba(46, 204, 113, 0.6);
    }
  }
`;

const ContactSection = styled.section`
  padding: 4rem 0;
  background: white;
`;

const ContactCard = styled.div`
  background: ${props => props.theme.colors.gradients.card};
  padding: 3rem 2rem;
  border-radius: 20px;
  text-align: center;
  
  h3 {
    font-size: 2rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    color: ${props => props.theme.colors.textLight};
    margin-bottom: 2rem;
    line-height: 1.6;
  }
  
  .contact-details {
    display: flex;
    justify-content: center;
    gap: 2rem;
    flex-wrap: wrap;
    
    .contact-item {
      text-align: center;
      
      .icon {
        font-size: 2rem;
        margin-bottom: 0.5rem;
      }
      
      .label {
        font-size: 0.9rem;
        color: ${props => props.theme.colors.textLight};
        margin-bottom: 0.25rem;
      }
      
      .value {
        font-weight: 600;
        color: ${props => props.theme.colors.text};
        
        a {
          color: ${props => props.theme.colors.primary};
          text-decoration: none;
          
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
`;

const Institutionen = () => {
  const services = [
    {
      id: 'regular-support',
      title: 'Regelmäßige tiergestützte Förderung',
      icon: '🐎',
      description: 'Je nach Gruppe werden unterschiedliche Lern- oder Förderziele ermittelt und in geeignete Spiele ausgearbeitet.',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      id: 'moving-school',
      title: '"Bewegte Schule" Lernen mit Tieren',
      icon: '🏃‍♀️',
      description: 'Lernen gelingt durch Wahrnehmen, Erleben und Bewegung am leichtesten. Ganzheitliche Erfahrungen bleiben im Gedächtnis.',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
    }
  ];

  return (
    <InstitutionenContainer>
      <Helmet>
        <title>Angebote für Institutionen - Der Ponystall | Tiergestützte Pädagogik</title>
        <meta name="description" content="Tiergestützte Pädagogik für Institutionen - Regelmäßige Förderung, Projekttage und bewegte Schule. Spielerisches Lernen mit Ponys für Kinder und Jugendliche." />
      </Helmet>

      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            Angebote für Institutionen
          </HeroTitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <div className="text-center">
            <h2 className="section-title">Tiergestützte Pädagogik für Institutionen</h2>
            <p className="section-subtitle">
              Der größte Teil der kindlichen Entwicklung geschieht über das Spiel, selbst Lernen von Theorie gelingt einfacher über das spielerische „Begreifen", das eigene „Tun".
            </p>
            <p className="section-subtitle">
              In unseren Angeboten für Institutionen übernimmt das Spiel eine tragende Rolle. Die Kinder werden in Situationen gebracht, in denen Sie das Lernen oder auch die Therapie nicht als solches wahrnehmen. Spaß und Spiel, gemeinsames Erleben und Erfahren mit den Tieren steht im Vordergrund.
            </p>
            <p className="section-subtitle">
              Die tiergestützte Pädagogik versteht sich als ganzheitliche Methode in der Arbeit mit Kindern und Jugendlichen, orientiert sich an einem positiven Menschenbild und nimmt das Kind in seinen Stärken wahr. Durch erlebnisorientierte Bewegungsförderung werden Erfolgserlebnisse ermöglicht die zu einer positiven Gesamtpersönlichkeitsentwicklung beitragen.
            </p>
          </div>

          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard
                key={service.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <ServiceIcon gradient={service.gradient}>
                  {service.icon}
                </ServiceIcon>
                <ServiceContent>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <Link to={`/institutionen/${service.id}`} className="contact-info">
                    Angebot ansehen
                  </Link>
                </ServiceContent>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </Container>
      </ContentSection>

      <ContactSection>
        <Container>
          <ContactCard>
            <h3>Kontakt</h3>
            <p>
              Informieren Sie sich über unsere Angebote und kontaktieren Sie uns für eine individuelle Beratung.
            </p>
            <div className="contact-details">
              <div className="contact-item">
                <div className="icon">📍</div>
                <div className="label">Adresse</div>
                <div className="value">Weidenweg 2, 26683 Saterland</div>
              </div>
              <div className="contact-item">
                <div className="icon">📞</div>
                <div className="label">Telefon</div>
                <div className="value">
                  <a href="tel:01621353794">0162 1353794</a> / <a href="tel:044987522">04498 7522</a>
                </div>
              </div>
              <div className="contact-item">
                <div className="icon">✉️</div>
                <div className="label">E-Mail</div>
                <div className="value">
                  <a href="mailto:derponystall@gmx.de">derponystall@gmx.de</a>
                </div>
              </div>
            </div>
          </ContactCard>
        </Container>
      </ContactSection>
    </InstitutionenContainer>
  );
};

export default Institutionen;